import { useEffect, useState } from "react";
import { ArrowUpRight, Plane, Truck, Zap, Container, Droplets, Wrench, GraduationCap, HeartHandshake } from "lucide-react";
import { LLAVE_ITEMS, IMG, waLink, track } from "@/data/content";
import { Reveal, SectionTag, TituloLineas, FotoRevelada } from "@/components/Reveal";
import Desplegable from "@/components/Desplegable";

const ICONOS_KIT = [Plane, Truck, Zap, Container, Droplets, Wrench, GraduationCap, HeartHandshake];
const NOMBRES_CORTOS = ["Dron Agras", "Chata remolque", "Generador", "Tanques de agua", "Mezcladora", "Repuestos", "Capacitación", "Acompañamiento"];

// «Llave en mano» en la portada: foto de una entrega, la frase, los 8
// elementos del paquete como íconos con una o dos palabras y, debajo, el
// desplegable «¿Qué es llave en mano?». El menú trae hasta este bloque y abre
// ese desplegable automáticamente.
export default function LlaveEnMano() {
  const [abierto, setAbierto] = useState(null);

  // El menú avisa y el desplegable se abre solo.
  useEffect(() => {
    const abrir = (e) => {
      if (e.detail?.bloque === "llave-en-mano") setAbierto("que-es");
    };
    window.addEventListener("certero:abrir-desplegable", abrir);
    return () => window.removeEventListener("certero:abrir-desplegable", abrir);
  }, []);

  return (
    <section
      id="llave-en-mano"
      className="scroll-mt-20 border-y border-hud bg-surface px-4 py-14 sm:px-8 sm:py-20 lg:px-12 lg:py-32"
    >
      <div className="mx-auto max-w-[1400px]">
        <Reveal>
          <SectionTag>Llave en mano</SectionTag>
          <TituloLineas
            lineas={["Todo el equipamiento,", "listo para operar"]}
            className="mt-5 max-w-2xl font-display text-2xl font-bold uppercase leading-tight tracking-tight text-mist sm:text-3xl lg:text-4xl"
          />
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-smoke sm:text-base">
            Todo lo que necesita para operar desde el primer día.
          </p>
        </Reveal>

        <Reveal delay={0.12} className="mt-10">
          <div className="group relative">
            <div className="absolute -bottom-3 -right-3 h-full w-full border border-certero/40" aria-hidden="true" />
            <img
              src={IMG.capacitacionPosventa}
              alt="Entrega de un dron Agras con capacitación al equipo del productor en depósito"
              loading="lazy"
              decoding="async"
              className="relative aspect-[16/9] w-full object-cover"
            />
          </div>
        </Reveal>

        {/* Los 8 elementos del paquete, de un vistazo */}
        <div className="mt-12 grid grid-cols-4 gap-px border border-hud bg-hud/40" data-testid="llave-kit-iconos">
          {LLAVE_ITEMS.map((item, i) => {
            const Icon = ICONOS_KIT[i];
            return (
              <Reveal key={item.t} delay={Math.min(0.3, i * 0.05)} className="bg-surface">
                <div className="flex h-full flex-col items-center gap-2 p-4 text-center sm:p-6">
                  <span className="flex h-10 w-10 items-center justify-center border border-hud bg-abyss sm:h-12 sm:w-12">
                    <Icon className="h-4 w-4 text-certero sm:h-5 sm:w-5" strokeWidth={1.5} />
                  </span>
                  <span className="font-display text-[11px] font-bold uppercase leading-tight tracking-wide text-mist sm:text-sm">
                    {NOMBRES_CORTOS[i]}
                  </span>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Desplegable con la explicación completa */}
        <Reveal delay={0.1} className="mt-10">
          <Desplegable
            id="que-es"
            titulo="¿Qué es llave en mano?"
            icono={HeartHandshake}
            compacto
            abierto={abierto === "que-es"}
            onToggle={(id) => setAbierto((a) => (a === id ? null : id))}
            testid="llave-desplegable-que-es"
          >
            <p className="flex-1 text-sm leading-relaxed text-smoke sm:text-base">
              CERTERO entrega alrededor del dron una solución completa: desde el vehículo y la
              energía hasta la capacitación de su personal, para que la primera jornada sea también
              la primera operación exitosa.
            </p>

            <div className="mt-6">
              {LLAVE_ITEMS.map((item, i) => (
                <div
                  key={item.t}
                  data-testid={`llave-en-mano-kit-item-${i}`}
                  className="flex items-start gap-5 border-b border-hud/70 py-4 last:border-b-0"
                >
                  <span className="font-mono text-xs tracking-[0.2em] text-certero">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1">
                    <h3 className="font-display text-base font-bold uppercase tracking-wide text-mist">{item.t}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-smoke">{item.d}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 border border-hud bg-abyss p-6" data-testid="llave-galeria-capacitacion">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-certero">Capacitación al productor</p>
              <div className="mt-4 grid grid-cols-3 gap-3">
                {[IMG.capacitacion01, IMG.capacitacion02, IMG.capacitacion03].map((src, i) => (
                  <FotoRevelada
                    key={src}
                    src={src}
                    alt={`Capacitación al equipo del productor en campo — sesión ${i + 1}`}
                    className="aspect-[4/5] w-full"
                  />
                ))}
              </div>
              <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.15em] text-smoke/70">Operación CERTERO</p>
            </div>

            <a
              data-testid="llave-en-mano-btn-cotizar-pack"
              href={waLink("Hola CERTERO SRL, quiero cotizar un paquete llave en mano completo para mi operación.")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("cotizar_llave_mano_whatsapp", {})}
              className="mt-7 inline-flex items-center gap-2 self-start rounded-full bg-certero px-8 py-4 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-[background-color,transform] duration-300 hover:bg-[#4bc443] active:scale-95"
            >
              Cotizar paquete llave en mano <ArrowUpRight className="h-4 w-4" />
            </a>
          </Desplegable>
        </Reveal>
      </div>
    </section>
  );
}
